/**
 *  @class
 *  @function SearchModal
 *
 *  Turns the header search field into a trigger that opens a modal holding the
 *  real search form. Any element carrying [data-search-modal-open] opens it,
 *  any element carrying [data-search-modal-close] closes it.
 */
if (!customElements.get('search-modal')) {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.search-modal--dialog');
      this.searchForm = this.querySelector('search-form');
      this.input = this.querySelector('input[type="search"]');
      this.openedBy = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      if (this.input) {
        this.input.addEventListener('input', () => this.setQueryState());
        this.setQueryState();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this.hideTimer);
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('search-modal-open');
    }

    onDocumentClick(event) {
      const closer = event.target.closest('[data-search-modal-close]');
      if (closer && this.contains(closer)) {
        event.preventDefault();
        this.close();
        return;
      }

      const opener = event.target.closest('[data-search-modal-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setQueryState() {
      this.classList.toggle('has-query', this.input.value.trim().length > 0);
    }

    open(opener) {
      if (this.isOpen) return;
      clearTimeout(this.hideTimer);

      this.openedBy = opener || null;
      if (this.openedBy) this.openedBy.setAttribute('aria-expanded', 'true');

      document.body.classList.add('overflow-hidden');
      // Lets CSS hide the PWA bottom tab bar, which otherwise floats over the
      // modal in standalone mode. See search-modal.css.
      document.body.classList.add('search-modal-open');
      this.setAttribute('open', '');

      // Flush styles so the modal is laid out (display:block, opacity:0) before
      // .is-open starts the fade — otherwise both land in one recalc and nothing
      // transitions. It also makes the element focusable right away; staying
      // synchronous keeps focus() inside the click gesture, which is what makes
      // iOS open the keyboard.
      this.getBoundingClientRect();
      this.classList.add('is-open');

      if (this.input) {
        this.input.focus({ preventScroll: true });
        if (this.input.value) this.input.select();
      } else {
        this.dialog.focus({ preventScroll: true });
      }
    }

    close() {
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      // Keep it displayed until the fade is done, then take it out of the layout
      // (and out of the tab order) for good. The PWA tab bar comes back at the
      // same moment, so it doesn't reappear over the still-fading modal.
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.removeAttribute('open');
        document.body.classList.remove('search-modal-open');
      }, 250);

      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('open-search');

      // Collapse any predictive results so the modal opens clean next time.
      if (this.searchForm && typeof this.searchForm.close === 'function') {
        this.searchForm.close();
      }

      if (this.openedBy) {
        this.openedBy.setAttribute('aria-expanded', 'false');
        this.openedBy.focus();
        this.openedBy = null;
      }
    }
  }

  customElements.define('search-modal', SearchModal);
}
